/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps */
import { Component, PropsWithChildren, ReactNode } from 'react';
import ErrorBoundaryFallback from 'pages/other/ErrorBoundaryFallback';
import { captureException } from 'services/posthog.service';
import { generateCorrelationId } from 'utils/error.util';

interface Props extends PropsWithChildren {
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  correlationId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  private correlationIdRef: string | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, correlationId: null };
  }

  static getDerivedStateFromError(error: Error) {
    const correlationId = generateCorrelationId();
    return { hasError: true, error, correlationId };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Store correlation ID in ref to ensure it's available for logging
    this.correlationIdRef = this.state.correlationId || generateCorrelationId();

    // Capture exception with additional context
    captureException(error, {
      correlationId: this.correlationIdRef,
      componentStack: errorInfo?.componentStack,
      errorInfo: errorInfo
    });

    // Update state with correlation ID if it wasn't set
    if (!this.state.correlationId) {
      this.setState({ correlationId: this.correlationIdRef });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          correlationId={this.state.correlationId}
        />
      );
    }
    return this.props.children;
  }
}
